/**
 * Screen recorder with MediaRecorder + timer controls.
 * Provides VP8 WebM output compatible with modern Safari and other browsers.
 * Uses the same toolbar as the desktop video recorder for visual consistency.
 */
import { useCallback, useMemo } from 'react';
import { Box, Stack, Typography, keyframes } from '@mui/material';
import { useScreenRecorder } from '@/components/ScreenRecorder/useScreenRecorder';
import { useCountdown } from '@/hooks/useCountdown';
import { useNavigationBlock } from '@/hooks/useNavigationBlock';
import { RecordingControls } from '@/pages/Record/Recorder/components';

type Props = {
  onChunk?: (chunk: Blob) => void;
  onComplete?: (file: File, durationSeconds: number) => void;
  includeMic?: boolean;
  preferredFps?: number;
  filenameBase?: string;
  maxDuration?: number;
};

const countdownPulse = keyframes`
  0% { transform: scale(1.4); opacity: 0.7; }
  60% { transform: scale(1); opacity: 1; }
  100% { transform: scale(0.95); opacity: 1; }
`;

export default function ScreenRecorder({
  onChunk,
  onComplete,
  includeMic = true,
  preferredFps = 30,
  filenameBase = 'recording',
  maxDuration = 0,
}: Props) {
  const {
    isRecording,
    isPaused,
    elapsedMs,
    error,
    isSupported,
    requestDisplayAccess,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    discardRecording,
  } = useScreenRecorder({
    onChunk,
    onComplete,
    includeMic,
    preferredFps,
    filenameBase,
    maxDuration,
  });

  const { countdownValue, isCountingDown, runCountdown, cancelCountdown } = useCountdown();

  const elapsedSeconds = useMemo(() => Math.max(0, elapsedMs / 1000), [elapsedMs]);

  // Block all navigation (SPA + browser close/refresh) while recording or counting down
  useNavigationBlock(
    isRecording || isCountingDown,
    'You have an active screen recording. Leaving this page will stop the recording and your progress will be lost. Continue?',
  );

  const handleStart = useCallback(async () => {
    if (isCountingDown || isRecording) return;

    // Acquire screen FIRST so the browser picker appears before the countdown
    let displayStream: MediaStream;
    try {
      displayStream = await requestDisplayAccess();
    } catch {
      // User cancelled the picker or permission denied — no action needed
      return;
    }

    const completed = await runCountdown();
    if (!completed) {
      // Countdown cancelled — clean up the pre-acquired stream
      displayStream.getTracks().forEach((track) => track.stop());
      return;
    }

    await startRecording(displayStream);
  }, [isCountingDown, isRecording, requestDisplayAccess, runCountdown, startRecording]);

  // Discard/Restart. Must use discardRecording, NOT stopRecording — stopping
  // finalizes the file and fires onComplete, which the page turns into an
  // auto-upload. i.e. "Discard" used to upload the recording being discarded.
  const handleReset = useCallback(() => {
    cancelCountdown();
    discardRecording();
  }, [cancelCountdown, discardRecording]);

  return (
    <Box sx={{ position: 'relative' }}>
      {countdownValue && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.4)',
            zIndex: 5,
            textAlign: 'center',
            borderRadius: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: 700,
              color: countdownValue === 'Go!' ? '#4ade80' : '#ffffff',
              textShadow: '0 0 40px rgba(255,255,255,0.5), 0 0 80px rgba(43,159,255,0.3)',
              fontSize: {
                xs: countdownValue === 'Go!' ? '70px' : '80px',
                sm: countdownValue === 'Go!' ? '100px' : '120px',
                md: countdownValue === 'Go!' ? '160px' : '200px',
              },
              animation: `${countdownPulse} 0.5s ease-out`,
            }}
          >
            {countdownValue}
          </Typography>
        </Box>
      )}
      <Stack spacing={1} alignItems="flex-start">
        <RecordingControls
          isRecording={isRecording}
          isPaused={isPaused}
          elapsed={elapsedSeconds}
          maxDuration={maxDuration}
          onStart={handleStart}
          onPause={pauseRecording}
          onResume={resumeRecording}
          onStop={stopRecording}
          onReset={handleReset}
          disabled={!isSupported || isCountingDown}
        />
        {!isSupported && (
          <Typography variant="body2" color="error">
            Screen recording is not supported in this browser.
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}
